import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "@aws-amplify/core";
import { useFeatures } from "flagged";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import {
  Card,
  EmptyLayout,
  SearchBar,
  Pagination,
} from "../../components/molecules";
import {
  deleteApplication,
  selectApplications,
  selectApplicationFilters,
} from "./index";
import { actions } from "./applications.reducers";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import {
  POPOVER_BUTTONS,
  ONE,
  DEFAULT_LIMIT,
  BADGE_VARIANTS,
  FRONTEND_INITIAL_STATUS,
  BACKEND_INITIAL_STATUS,
} from "../../constants";
import {
  menuDeleteIcon,
  menuDuplicateIcon,
  viewIcon,
  folderIcon,
} from "../../assets/icons";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as DrawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { fetchDataModels } from "../DataModels";
import { selectRecentSelectedApp } from "./applications.selectors";

const { DELETE, DUPLICATE, VIEW } = POPOVER_BUTTONS;
const {
  setSelectedAppId,
  setSelectedApp,
  setShouldShowMenuBar,
  setApplicationFilters,
  setAppPipelineStatus,
} = actions;
const { setSelectedModal, setItemToBeDeleted, setShouldShowDeploymentModal } =
  modalActions;
const { setSelectedDrawer } = DrawerActions;
const { setSelectedTab } = breadCrumbActions;
const { DELETE_MODAL } = MODALS;
const { MANAGE_APP_DRAWER } = DRAWERS;
const { DELETE_APPLICATION, FETCH_APPLICATIONS, CREATE_APPLICATION } =
  SPINNER_NAMES;
const { SUCCESS, PRIMARY } = BADGE_VARIANTS;

const Applications = () => {
  const currentPath = window.location.pathname;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showApplicationSettings, showAppDataModal } = useFeatures();
  const recentSelectedApp = useSelector(selectRecentSelectedApp);

  const [pageCount, setPageCount] = useState();
  const [shouldShowAppCards, setShouldShowAppCards] = useState(true);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const scrollRef = useRef();

  const {
    data: applicationsData,
    count: applicationsCount,
    total: totalApplications,
  } = useSelector(selectApplications);
  const filters = useSelector(selectApplicationFilters);

  const getRouteToNavigate = (appId) => {
    if (showAppDataModal) return `/applications/${appId}/app-data-modal`;
    else if (showApplicationSettings) return `/applications/${appId}/settings`;
  };

  const popOverOptions = (app) => [
    {
      name: VIEW,
      icon: viewIcon,
      handleOnClick: () => {
        dispatch(setShouldShowMenuBar(true));
        navigate(getRouteToNavigate(app.id));
      },
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: () => console.log("Clicked on Duplicate option."),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(app));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  useEffect(() => {
    setPageCount(Math.ceil(applicationsCount / DEFAULT_LIMIT));
  }, [applicationsCount]);

  useEffect(() => {
    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [filters]);

  useEffect(() => {
    return () => {
      dispatch(setApplicationFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSelectedAppId(null));
    dispatch(setSelectedApp(null));
    dispatch(setShouldShowMenuBar(false));
    dispatch(setSelectedTab(null));
  }, [dispatch, currentPath]);

  useEffect(() => {
    setShouldShowAppCards(!isEmpty(applicationsData));
  }, [applicationsData]);

  const handleCreateNewApp = useCallback(() => {
    dispatch(setSelectedDrawer(MANAGE_APP_DRAWER));
  }, [dispatch]);

  const handlePagination = useCallback(
    (value) =>
      dispatch(setApplicationFilters({ ...filters, page: value + ONE })),
    [dispatch, filters]
  );

  const handleOnSearch = useCallback(
    (value) => {
      dispatch(
        setApplicationFilters({
          ...filters,
          name: value.trim(),
          page: ONE,
        })
      );
    },
    [dispatch, filters]
  );

  const handleDeleteApp = useCallback(
    (app) => {
      dispatch(deleteApplication(app.id)).then((res) => {
        if (res.payload) {
          setShouldShowDeleteModal(false);
          dispatch(fetchDataModels());
          if (recentSelectedApp?.id === app.id) {
            dispatch(setShouldShowDeploymentModal(false));
            dispatch(
              setAppPipelineStatus({
                frontend: FRONTEND_INITIAL_STATUS,
                backend: BACKEND_INITIAL_STATUS,
              })
            );
          }
        }
      });
    },
    [dispatch, recentSelectedApp?.id]
  );
  const getBadges = (application) => [
    {
      color: SUCCESS,
      value: application.status,
    },
    {
      color: PRIMARY,
      value: application?.billingType || "free",
    },
  ];

  const renderAppCards = () => (
      <div className="min-h-[70vh] mb-4">
        <div className="grid grid-cols-12 gap-4 w-full">
          {applicationsData.map((application) => (
            <Card
              key={application.id}
              name={application.name}
              badges={getBadges(application)}
              createdAt={application.createdAt}
              popOverOptions={popOverOptions(application)}
              onClick={() => {
                navigate(getRouteToNavigate(application.id));
              }}
            />
          ))}
        </div>
      </div>
  );

  const renderAppLayout = () => {
    return shouldShowAppCards ? (
      renderAppCards()
    ) : (
      <EmptyLayout
        title="Welcome to Tava"
        subTitle="You can get started by creating a new Application"
        buttonText="Create New Application"
        image={folderIcon}
        handleOnClick={handleCreateNewApp}
      />
    );
  };

  return (
    <div className="scrollbar content overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title>Application | TavaJS</title>
      </Helmet>
      <div
        className={classNames(
          "flex-col xs:flex-row flex justify-end mb-4 gap-4",
          { "justify-between": !!totalApplications }
        )}
        ref={scrollRef}
      >
        {!!totalApplications && (
          <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
        )}
        <button
          type="button"
          className="btn btn-primary w-full xs:w-auto"
          disabled={isSpinnerActive}
          onClick={() => {
            dispatch(setSelectedDrawer(MANAGE_APP_DRAWER));
          }}
        >
          <Spinner
            name={CREATE_APPLICATION}
            setIsSpinnerActive={setIsSpinnerActive}
            persistSize
          >
            Create New Application
          </Spinner>
        </button>
      </div>
      <Spinner name={FETCH_APPLICATIONS}>{renderAppLayout()}</Spinner>

      {shouldShowDeleteModal && (
        <DeleteConfirmationModal
          handleDelete={handleDeleteApp}
          setShouldShowDeleteModal={setShouldShowDeleteModal}
          title="Delete Application"
          spinner={DELETE_APPLICATION}
        />
      )}
      <Pagination
        handlePagination={handlePagination}
        pagesCount={pageCount}
        activePage={filters.page - ONE}
        count={applicationsCount}
      />
    </div>
  );
};

export default Applications;
